const PHOTO_EXTENSIONS = /\.(jpe?g|png|gif|webp)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|mov|webm)$/i;
const AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

export function albumSelectionError(entries, usage, photos) {
  const photoEntries = entries.filter(entry => !entry.kind || entry.kind === "photo");
  if (!usage.upload_allowed) return { message: "Uploads are blocked. Renew your plan to add more photos." };
  if (usage.max_album_photos != null && photoEntries.length > Math.max(0, usage.max_album_photos - usage.album_photos)) return {
    message: "Plan photo limit: {remaining} slots remain across all albums; you selected {selected}. Remove photos or purchase a larger plan.",
    values: { remaining: Math.max(0, usage.max_album_photos - usage.album_photos), selected: photoEntries.length },
  };
  const remaining = Math.max(0, usage.max_photos_per_album - photos.filter(item => !item.kind || item.kind === "photo").length);
  if (photoEntries.length > remaining) return {
    message: "Photo count limit: this album allows {limit} photos. {remaining} slots remain; you selected {selected}. Deselect files or ask the administrator to increase the album photo limit.",
    values: { limit: usage.max_photos_per_album, remaining, selected: photoEntries.length },
  };
  const remainingFiles = Math.max(0, usage.max_files - usage.files);
  if (entries.length > remainingFiles) return {
    message: "Account file count limit: {remaining} slots remain; you selected {selected} photos. Remove stored files or contact the administrator.",
    values: { remaining: remainingFiles, selected: entries.length },
  };
  const selectedBytes = entries.reduce((sum, entry) => sum + entry.file.size, 0);
  const remainingBytes = Math.max(0, usage.max_bytes - usage.bytes);
  if (selectedBytes > remainingBytes) return {
    message: "Storage limit: {remaining} is available, but the selected photos total {selected}. Deselect files or free up storage.",
    values: { remaining: formatFileSize(remainingBytes), selected: formatFileSize(selectedBytes) },
  };
  return null;
}

export function albumRequestId() {
  if (globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function prepareAlbumFiles(files, maxPhotoMB) {
  return Array.from(files).map((file) => {
    const kind = PHOTO_EXTENSIONS.test(file.name) ? "photo" : VIDEO_EXTENSIONS.test(file.name) ? "video" : AUDIO_EXTENSIONS.test(file.name) ? "audio" : null;
    const reason = !kind ? "Unsupported media file."
      : kind === "photo" && maxPhotoMB != null && file.size > maxPhotoMB * 1024 * 1024 ? "File exceeds the photo size limit." : "";
    return { file, kind, id: albumRequestId(), selected: !reason, status: reason ? "skipped" : "pending", progress: 0, error: reason };
  });
}

// Two workers, with starts spaced to stay below the API's 20 uploads/minute limit.
export async function uploadAlbumFiles({ entries, albumId, api, signal, onUpdate, spacing = 3200 }) {
  let cursor = 0;
  let nextStart = 0;
  const wait = (delay) => new Promise((resolve) => {
    if (signal.aborted) { resolve(); return; }
    const done = () => { clearTimeout(timer); signal.removeEventListener("abort", done); resolve(); };
    const timer = setTimeout(done, delay);
    signal.addEventListener("abort", done, { once: true });
  });
  const worker = async () => {
    while (cursor < entries.length && !signal.aborted) {
      const entry = entries[cursor++];
      const start = Math.max(Date.now(), nextStart); nextStart = start + spacing;
      await wait(Math.max(0, start - Date.now()));
      if (signal.aborted) break;
      onUpdate(entry.id, { status: "uploading", error: "", progress: 0 });
      const body = new FormData(); body.append("file", entry.file);
      try {
        await api.post(`/albums/${albumId}/photos`, body, {
          signal, headers: { "X-Upload-Id": entry.id },
          onUploadProgress: ({ loaded, total }) => onUpdate(entry.id, { progress: total ? Math.min(99, Math.round(loaded / total * 100)) : 0 }),
        });
        onUpdate(entry.id, { status: "uploaded", progress: 100 });
      } catch (error) {
        onUpdate(entry.id, { status: signal.aborted ? "cancelled" : "failed", error: error.response?.data?.detail || "Upload interrupted. Retry this file." });
      }
    }
  };
  await Promise.all([worker(), worker()]);
  if (signal.aborted) entries.forEach((entry) => onUpdate(entry.id, { cancelledIfPending: true }));
}
